import findIndex from 'lodash/findIndex';

import type {
  InitializeAction,
  FetchSuccessAction,
  FetchFailureAction,
  DeleteItemsAction,
  MoveItemAction,
  ReplaceItemAction,
  SetParamAction,
  InsertItemsAction,
  BaseStream,
  FetchPreviousSuccessActionParams,
  EmptyStreamAction,
  ResetStreamAction,
  FetchAllSuccessActionParams,
} from './types';

import {
  STREAM__SET_PARAM,
  STREAM__FETCH_FAILURE,
  STREAM__DELETE_ITEMS,
  STREAM__REPLACE_ITEM,
  STREAM__MOVE_ITEM,
  STREAM__INSERT_ITEMS,
  replaceItems,
  markAsFetching,
  markAsCancelled,
  STREAM__FETCH_RETRY,
  fetchRetry,
} from './actions';
import type { Action } from 'redux-actions';

export const handleInitAction = <T extends BaseStream>(
  streamState: T,
  action: InitializeAction
): T => {
  const {
    streamId,
    streamParams = {},
    windowSize,
    streamType,
    groupId,
    itemsPerFetch,
    items = [],
    total,
    hasMore = false,
    hasLess = false,
    fetchNextCallback,
    fetchPreviousCallback,
    fetchAllCallback,
  } = action;
  return {
    ...streamState,
    streamId,
    streamParams,
    streamType,
    groupId,
    itemsPerFetch,
    windowSize,
    items,
    total,
    hasMore,
    hasLess,

    // store only the action type as the callback itself doesn't serialize after ssr
    // since the signature is fixed, we can easily re-create the action creators anyway
    fetchNextCallback: fetchNextCallback?.toString(),
    fetchPreviousCallback: fetchPreviousCallback?.toString(),
    fetchAllCallback: fetchAllCallback?.toString(),
  };
};

export const handleFetchAction = <T extends BaseStream>(streamState: T): T => ({
  ...streamState,
  isFetching: true,
});

export const handleFetchSuccessAction = <T extends BaseStream>(
  streamState: T,
  action: FetchSuccessAction
): T => {
  const { windowSize } = streamState;
  let items = action.reset
    ? [...action.items]
    : [...(streamState.items || []), ...(action.items || [])];
  const total = action.total !== undefined ? action.total : items.length;

  if (windowSize !== undefined && items.length > windowSize) {
    items = items.slice(items.length - windowSize, items.length - 1);
  }

  return {
    ...streamState,
    isFetching: false,
    items,
    total,
    hasLess: action.hasLess,
    hasMore: action.hasMore,
    errorMsg: undefined,
    fetchFailuresConsecutive: 0,
  };
};

export const handleFetchPreviousSuccessAction = <T extends BaseStream>(
  streamState: T,
  {
    payload: { items, dedupeFunction = () => true, hasLess },
  }: Action<FetchPreviousSuccessActionParams>
): T => {
  const { windowSize } = streamState;
  const dedupedItems = items.filter(item =>
    dedupeFunction(item, streamState.items)
  );
  if (!dedupedItems.length) {
    return { ...streamState, fetchFailuresConsecutive: 0 };
  }

  let updatedItems = [...dedupedItems, ...streamState.items];

  if (windowSize !== undefined && updatedItems.length > windowSize) {
    updatedItems = updatedItems.slice(0, windowSize);
  }

  return {
    ...streamState,
    isFetching: false,
    fetchFailuresConsecutive: 0,
    items: updatedItems,
    hasLess,
  };
};

export const handleFetchAllSuccessAction = <T extends BaseStream>(
  streamState: T,
  { payload: { items } }: Action<FetchAllSuccessActionParams>
): T => {
  return {
    ...streamState,
    isFetching: false,
    fetchFailuresConsecutive: 0,
    items,
    hasLess: false,
    hasMore: false,
  };
};

export const handleFetchFailureAction = <T extends BaseStream>(
  streamState: T,
  action: FetchFailureAction
): T => ({
  ...streamState,
  isFetching: false,
  errorMsg: action.errorMsg,
  failureFetchType: action.fetchType,
  fetchFailuresConsecutive: (streamState.fetchFailuresConsecutive || 0) + 1,
});

export const handleFetchRetryAction = <T extends BaseStream>(
  streamState: T,
  _action: ReturnType<typeof fetchRetry>
): T => ({
  ...streamState,
  errorMsg: undefined,
  failureFetchType: undefined,
});

export const handleDeleteItemsAction = <T extends BaseStream>(
  streamState: T,
  action: DeleteItemsAction
): T => {
  const items = streamState.items ?? [];
  const isEqual = action.isEqual ?? ((a, b) => a === b);

  const updatedItems = items.filter(
    item => !action.items.some(i => isEqual(item, i))
  );
  const removedCount = items.length - updatedItems.length;
  const updatedTotal = (streamState.total || 0) - removedCount;
  return {
    ...streamState,
    items: updatedItems,
    total: updatedTotal > 0 ? updatedTotal : 0,
  };
};

export const handleMoveItemAction = <T extends BaseStream>(
  streamState: T,
  action: MoveItemAction
): T => {
  const { items } = streamState;
  const { position, item } = action;
  const itemPosition = items.indexOf(item);
  if (itemPosition === position) {
    return streamState;
  }
  const updatedItems = [...items];
  updatedItems.splice(itemPosition, 1);
  updatedItems.splice(position, 0, item);
  return {
    ...streamState,
    items: updatedItems,
  };
};

export const handleReplaceItemAction = <T extends BaseStream>(
  streamState: T,
  action: ReplaceItemAction
): T => {
  const itemIndex = findIndex(streamState.items || [], action.predicate);

  if (itemIndex === -1) {
    return streamState;
  }

  const newItems = [...streamState.items];
  newItems[itemIndex] = action.newItem;

  return {
    ...streamState,
    items: newItems,
  };
};

export const handleInsertItemsAction = <T extends BaseStream>(
  streamState: T,
  action: InsertItemsAction
): T => {
  const sorted = action.newItems.slice();
  sorted.sort((a, b) => {
    if (a.offset === b.offset) {
      return 0;
    }
    return a.offset > b.offset ? 1 : -1;
  });
  const newItems = [...streamState.items];
  let inserted = 0;
  for (const elem of sorted) {
    if (action.deduplicate && newItems.includes(elem.item)) {
      continue;
    }
    inserted++;
    const offset = Math.max(elem.offset, 0);
    if (offset >= newItems.length) {
      newItems.push(elem.item);
    } else {
      newItems.splice(offset, 0, elem.item);
    }
  }
  return {
    ...streamState,
    total: (streamState.total || 0) + inserted,
    items: newItems,
  };
};

export const handleSetParam = <T extends BaseStream>(
  streamState: T,
  action: SetParamAction
): T => ({
  ...streamState,
  streamParams: {
    ...streamState.streamParams,
    [action.paramType]: action.paramValue,
  },
});

export const handleReplaceItems = <T extends BaseStream>(
  streamState: T,
  { payload: { updatedItems } }: ReturnType<typeof replaceItems>
) => ({
  ...streamState,
  items: updatedItems,
});

export const handleMarkAsFetching = <T extends BaseStream>(
  streamState: T,
  action: ReturnType<typeof markAsFetching>
): T => ({
  ...streamState,
  isFetching: true,
});

export const handleMarkAsCancelled = <T extends BaseStream>(
  streamState: T,
  action: ReturnType<typeof markAsFetching>
): T => ({
  ...streamState,
  isFetching: false,
});

export const handleEmptyStream = <T extends BaseStream>(
  streamState: T,
  action: EmptyStreamAction
): T => ({
  ...streamState,
  isFetching: false,
  items: [],
  total: 0,
  hasMore: false,
  hasLess: false,
  fetchFailuresConsecutive: 0,
});

export const handleResetStream = <T extends BaseStream>(
  streamState: T,
  action: ResetStreamAction
): T => ({
  ...streamState,
  isFetching: false,
  items: [],
  hasMore: true,
  hasLess: false,
  prevCursor: undefined,
  cursor: undefined,
  fetchFailuresConsecutive: 0,
});

// installed to reducer of every stream type as they are
export const commonActionHandlers = {
  [STREAM__FETCH_FAILURE]: <T extends BaseStream = BaseStream>(
    streamState: T,
    action: FetchFailureAction
  ) => handleFetchFailureAction(streamState, action),
  [STREAM__FETCH_RETRY]: <T extends BaseStream = BaseStream>(
    streamState: T,
    action: ReturnType<typeof fetchRetry>
  ) => handleFetchRetryAction(streamState, action),
  [STREAM__MOVE_ITEM]: <T extends BaseStream = BaseStream>(
    streamState: T,
    action: MoveItemAction
  ) => handleMoveItemAction(streamState, action),
  [STREAM__DELETE_ITEMS]: <T extends BaseStream = BaseStream>(
    streamState: T,
    action: DeleteItemsAction
  ) => handleDeleteItemsAction(streamState, action),
  [STREAM__REPLACE_ITEM]: <T extends BaseStream = BaseStream>(
    streamState: T,
    action: ReplaceItemAction
  ) => handleReplaceItemAction(streamState, action),
  [STREAM__SET_PARAM]: <T extends BaseStream = BaseStream>(
    streamState: T,
    action: SetParamAction
  ) => handleSetParam(streamState, action),
  [STREAM__INSERT_ITEMS]: <T extends BaseStream = BaseStream>(
    streamState: T,
    action: InsertItemsAction
  ) => handleInsertItemsAction(streamState, action),
  [replaceItems.toString()]: handleReplaceItems,
  [markAsFetching.toString()]: handleMarkAsFetching,
  [markAsCancelled.toString()]: handleMarkAsCancelled,
};
